/**
 * WalkableViewer — TRUE 3D navigable panorama using Depth AI (Fase 3, Feb 2026).
 *
 * Given a 360° equirectangular panorama + its depth map (from Depth Anything V2),
 * reconstructs a displaced sphere mesh where each vertex position is offset by the
 * depth value. Then allows the user to move the camera in a limited 3D volume
 * (WASD / touch swipe / gamepad-ready), producing REAL PARALLAX — objects nearer
 * to the original camera position shift more than far ones, exactly like Matterport.
 *
 * Props:
 *   - panorama            : { id, image_base64, nome, depth_map_base64?, foto_360? }
 *   - authToken           : session token to auto-generate depth map if missing
 *   - onClose()
 */
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { X, Loader2, Zap, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Movement bounds (in scene units — the panorama sphere has radius 1)
const MAX_WALK_RADIUS = 0.35;   // 35cm-equivalent horizontal movement
const MAX_WALK_HEIGHT = 0.15;   // small vertical bob range
const MOVE_SPEED = 0.008;       // per keydown tick
const DAMPING = 0.90;

export default function WalkableViewer({ panorama, authToken, onClose }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const velocityRef = useRef({ x: 0, y: 0, z: 0 });
  const keysRef = useRef({ w: false, a: false, s: false, d: false, q: false, e: false });
  const yawRef = useRef(0);
  const pitchRef = useRef(0);
  const rafRef = useRef(null);
  const dragRef = useRef({ active: false, x: 0, y: 0 });

  const [status, setStatus] = useState("init"); // init | generating | ready | error
  const [statusMsg, setStatusMsg] = useState("");
  const [depthData, setDepthData] = useState(null); // base64 data URL of grey depth map

  // 1) Ensure we have a depth map (auto-generate if missing)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (panorama.depth_map_base64) {
        setDepthData(panorama.depth_map_base64);
        setStatus("ready");
        return;
      }
      setStatus("generating");
      setStatusMsg("Genero depth map con Depth Anything V2 (Replicate). Attendere 5-15 secondi…");
      try {
        const r = await axios.post(`${API}/design/depth/estimate?token=${authToken}`,
          { image_base64: panorama.image_base64, panorama_id: panorama.id },
          { timeout: 180000 });
        if (cancelled) return;
        if (r.data.needs_configuration) {
          setStatus("error");
          setStatusMsg("Depth AI non configurato. Chiedi all'amministratore di aggiungere REPLICATE_API_TOKEN.");
          return;
        }
        setDepthData(r.data.depth_map_base64);
        setStatus("ready");
        toast.success(r.data.cached ? "Depth map caricata dalla cache" : "Depth map generata! Costo ~$0.003");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setStatusMsg(e.response?.data?.detail || "Errore generazione depth map");
      }
    })();
    return () => { cancelled = true; };
  }, [panorama.id, panorama.depth_map_base64, panorama.image_base64, authToken]);

  // 2) Build Three.js scene once depth is ready
  useEffect(() => {
    if (status !== "ready" || !containerRef.current || !depthData) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.01, 100);
    camera.position.set(0, 0, 0);
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(window.devicePixelRatio); // uncapped for max sharpness on Retina/4K
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping; // preserve natural photo colors
    container.appendChild(renderer.domElement);

    // Detect max anisotropy for max texture sharpness
    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    // Load color panorama + depth map as textures — HIGH QUALITY config
    const texLoader = new THREE.TextureLoader();
    const colorTex = texLoader.load(panorama.image_base64);
    colorTex.colorSpace = THREE.SRGBColorSpace;
    colorTex.wrapS = THREE.RepeatWrapping;
    colorTex.wrapT = THREE.ClampToEdgeWrapping;
    colorTex.anisotropy = maxAnisotropy;         // usually 16x on modern GPUs
    colorTex.minFilter = THREE.LinearMipmapLinearFilter;
    colorTex.magFilter = THREE.LinearFilter;
    colorTex.generateMipmaps = true;

    const depthTex = texLoader.load(depthData);
    depthTex.wrapS = THREE.RepeatWrapping;
    depthTex.wrapT = THREE.ClampToEdgeWrapping;
    depthTex.minFilter = THREE.LinearFilter; // no mipmap on depth to avoid discontinuities
    depthTex.magFilter = THREE.LinearFilter;
    depthTex.generateMipmaps = false;

    // Higher tessellation for smoother displacement (512x256 = 130k verts, still 60fps)
    const geo = new THREE.SphereGeometry(1, 512, 256);
    geo.scale(-1, 1, 1); // invert to render inside

    // Custom shader: color from panorama, displaced radially by depth luminance
    const uniforms = {
      colorMap: { value: colorTex },
      depthMap: { value: depthTex },
      displacement: { value: 0.35 }, // moderate default → less stretching artifacts
      minDepth: { value: 0.05 },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        uniform sampler2D depthMap;
        uniform float displacement;
        uniform float minDepth;
        void main() {
          vUv = uv;
          // Bilinear sample of depth (already linear-filtered)
          vec4 d = texture2D(depthMap, uv);
          float closeness = d.r; // 1 = near, 0 = far
          float distance = 1.0 - closeness;
          // Smooth curve to soften transitions between near and far regions
          float smoothD = smoothstep(minDepth, 1.0, distance);
          float scale = mix(0.90, 1.0 + displacement, smoothD);
          vec3 displaced = normalize(position) * scale;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform sampler2D colorMap;
        void main() {
          gl_FragColor = texture2D(colorMap, vUv);
        }
      `,
      side: THREE.FrontSide,
    });

    const mesh = new THREE.Mesh(geo, mat);
    scene.add(mesh);

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    meshRef.current = { mesh, uniforms };

    // ---- INPUT: keyboard, mouse drag, touch ----
    const onKey = (e, down) => {
      const k = e.key.toLowerCase();
      if (k === "w" || k === "arrowup")    { keysRef.current.w = down; e.preventDefault(); }
      if (k === "s" || k === "arrowdown")  { keysRef.current.s = down; e.preventDefault(); }
      if (k === "a" || k === "arrowleft")  { keysRef.current.a = down; e.preventDefault(); }
      if (k === "d" || k === "arrowright") { keysRef.current.d = down; e.preventDefault(); }
      if (k === "q")                       { keysRef.current.q = down; }
      if (k === "e")                       { keysRef.current.e = down; }
      if (k === "escape" && down)          { onClose(); }
    };
    const onKD = (e) => onKey(e, true);
    const onKU = (e) => onKey(e, false);
    window.addEventListener("keydown", onKD);
    window.addEventListener("keyup", onKU);

    // Mouse drag = look around
    const canvas = renderer.domElement;
    const onDown = (e) => {
      dragRef.current.active = true;
      const p = e.touches ? e.touches[0] : e;
      dragRef.current.x = p.clientX; dragRef.current.y = p.clientY;
    };
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - dragRef.current.x;
      const dy = p.clientY - dragRef.current.y;
      dragRef.current.x = p.clientX; dragRef.current.y = p.clientY;
      yawRef.current -= dx * 0.005;
      pitchRef.current = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, pitchRef.current - dy * 0.005));
    };
    const onUp = () => { dragRef.current.active = false; };
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    canvas.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);

    // Wheel = FOV zoom
    const onWheel = (e) => {
      e.preventDefault();
      camera.fov = Math.max(30, Math.min(110, camera.fov + e.deltaY * 0.05));
      camera.updateProjectionMatrix();
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });

    // Resize
    const onResize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    // ---- ANIMATION LOOP ----
    const loop = () => {
      // Apply keyboard movement in camera-forward/right directions
      const forward = new THREE.Vector3(-Math.sin(yawRef.current), 0, -Math.cos(yawRef.current));
      const right = new THREE.Vector3(Math.cos(yawRef.current), 0, -Math.sin(yawRef.current));
      const v = velocityRef.current;
      if (keysRef.current.w) { v.x += forward.x * MOVE_SPEED; v.z += forward.z * MOVE_SPEED; }
      if (keysRef.current.s) { v.x -= forward.x * MOVE_SPEED; v.z -= forward.z * MOVE_SPEED; }
      if (keysRef.current.d) { v.x += right.x * MOVE_SPEED; v.z += right.z * MOVE_SPEED; }
      if (keysRef.current.a) { v.x -= right.x * MOVE_SPEED; v.z -= right.z * MOVE_SPEED; }
      if (keysRef.current.q) { v.y -= MOVE_SPEED; }
      if (keysRef.current.e) { v.y += MOVE_SPEED; }
      // Damping + integrate
      v.x *= DAMPING; v.y *= DAMPING; v.z *= DAMPING;
      camera.position.x += v.x; camera.position.y += v.y; camera.position.z += v.z;
      // Clamp inside allowed walk radius
      const r = Math.sqrt(camera.position.x ** 2 + camera.position.z ** 2);
      if (r > MAX_WALK_RADIUS) {
        camera.position.x *= MAX_WALK_RADIUS / r;
        camera.position.z *= MAX_WALK_RADIUS / r;
        v.x = 0; v.z = 0;
      }
      camera.position.y = Math.max(-MAX_WALK_HEIGHT, Math.min(MAX_WALK_HEIGHT, camera.position.y));

      // Camera look direction
      const lookX = Math.sin(yawRef.current) * Math.cos(pitchRef.current);
      const lookY = Math.sin(pitchRef.current);
      const lookZ = Math.cos(yawRef.current) * Math.cos(pitchRef.current);
      camera.lookAt(camera.position.x + lookX, camera.position.y + lookY, camera.position.z + lookZ);

      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", onKD);
      window.removeEventListener("keyup", onKU);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("resize", onResize);
      canvas.removeEventListener("mousedown", onDown);
      canvas.removeEventListener("touchstart", onDown);
      canvas.removeEventListener("wheel", onWheel);
      try {
        renderer.dispose();
        geo.dispose();
        mat.dispose();
        colorTex.dispose();
        depthTex.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      } catch (_) { /* noop */ }
    };
  }, [status, depthData, panorama.image_base64, onClose]);

  // Displacement strength control (slider)
  const setDisplacement = (v) => {
    if (meshRef.current?.uniforms) {
      meshRef.current.uniforms.displacement.value = Number(v);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black" data-testid="walkable-viewer">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-3 flex items-center justify-between text-white pointer-events-none">
        <div className="pointer-events-auto">
          <div className="font-semibold text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-400" /> 3D Walk Mode — {panorama.nome || "Panorama"}
          </div>
          <div className="text-[10px] text-white/70 mt-0.5">Depth Anything V2 · Parallax reale · WASD per camminare · Mouse drag per guardarti attorno</div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          {status === "ready" && (
            <div className="hidden md:flex items-center gap-2 bg-white/10 border border-white/30 rounded px-2 py-1">
              <span className="text-[10px] text-white/70">Effetto parallax</span>
              <input type="range" min="0" max="1.0" step="0.05" defaultValue="0.35"
                onChange={e => setDisplacement(e.target.value)}
                className="w-24 accent-yellow-400" data-testid="walk-displacement"
                title="0 = qualità foto originale · 0.35 = default bilanciato · 1.0 = massima profondità (più stretching)" />
              <span className="text-[9px] text-white/50">0 = qualità 100%</span>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={onClose}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="walk-close">
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
        </div>
      </div>

      {/* Viewer canvas mount point */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Loading state */}
      {status === "generating" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 text-white text-center p-6">
          <div className="max-w-md">
            <Loader2 className="h-10 w-10 mx-auto mb-3 animate-spin text-yellow-400" />
            <div className="font-semibold text-lg mb-1">Generazione depth map in corso…</div>
            <div className="text-sm text-white/70">{statusMsg}</div>
            <div className="text-[11px] text-white/50 mt-3">Prima volta: 5-15 secondi · Successivi: istantanea (cache)</div>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 text-white text-center p-6">
          <div className="max-w-md">
            <div className="font-semibold text-lg mb-2">Errore</div>
            <div className="text-sm text-white/70 mb-4">{statusMsg}</div>
            <Button onClick={onClose} variant="outline" className="bg-white/10 border-white/30 text-white">Chiudi</Button>
          </div>
        </div>
      )}

      {/* Instructions overlay (bottom center) */}
      {status === "ready" && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <div className="bg-black/60 backdrop-blur border border-white/20 rounded-lg px-3 py-1.5 text-white text-xs flex items-center gap-3">
            <span className="flex items-center gap-1"><Info className="h-3 w-3" /> Controlli:</span>
            <kbd className="px-1.5 py-0.5 bg-white/20 rounded font-mono text-[10px]">W A S D</kbd>
            <span>cammina</span>
            <kbd className="px-1.5 py-0.5 bg-white/20 rounded font-mono text-[10px]">Q E</kbd>
            <span>sopra/sotto</span>
            <kbd className="px-1.5 py-0.5 bg-white/20 rounded font-mono text-[10px]">Mouse</kbd>
            <span>guarda</span>
            <kbd className="px-1.5 py-0.5 bg-white/20 rounded font-mono text-[10px]">Rotella</kbd>
            <span>zoom</span>
          </div>
        </div>
      )}
    </div>
  );
}
